export interface AuthenticatedApiFunctionOptions {
  token?: string;
  figmaToken?: string | null;
  linearToken?: string | null;
}

export type AuthenticatedApiFunction<TArgs extends any[], TReturn> = (
  ...args: TArgs
) => Promise<TReturn>;

export type WrappedApiFunction<TArgs extends any[], TReturn> = (
  options: AuthenticatedApiFunctionOptions,
  ...args: TArgs
) => Promise<TReturn>;

/**
 * Wraps an API function to automatically include Authorization header with JWT token.
 * The wrapped function will receive the token as the first parameter.
 * 
 * Example usage:
 * 
 * 1. Create your API function with auth options as first parameter:
 * async function myApiFunction(
 *   options: AuthenticatedApiFunctionOptions,
 *   data: MyDataType
 * ): Promise<MyResponseType> {
 *   const headers = new Headers();
 *   if (options.token) {
 *     headers.set('Authorization', `Bearer ${options.token}`);
 *   }
 *   // ... rest of your fetch logic
 * }
 * 
 * 2. Create authenticated version:
 * export const authenticatedMyApiFunction = withAuth(myApiFunction);
 * 
 * 3. Use in hooks/components with token from loader:
 * const { token, figmaToken } = useLoaderData<AuthState>();
 * const result = await authenticatedMyApiFunction(token, figmaToken)(data);
 */
export function withAuth<TArgs extends any[], TReturn>(
  apiFunction: WrappedApiFunction<TArgs, TReturn>
): (token: string | undefined, figmaToken?: string | null, linearToken?: string | null) => AuthenticatedApiFunction<TArgs, TReturn> {
  return (token: string | undefined, figmaToken?: string | null, linearToken?: string | null) => {
    return (...args: TArgs): Promise<TReturn> => {
      return apiFunction({ token, figmaToken, linearToken }, ...args);
    };
  };
} 